import { useEffect, useState } from 'react'
import { Box, HStack, VStack, Text, Progress, IconButton } from '@chakra-ui/react'
import { X, File, CheckCircle, XCircle, Spinner } from '@phosphor-icons/react'

interface UploadProgressProps {
  fileName: string
  progress: number
  status: 'pending' | 'uploading' | 'completed' | 'error'
  error?: string
  onCancel?: () => void
  onDismiss?: () => void
}

export function UploadProgressItem({ 
  fileName, 
  progress, 
  status, 
  error, 
  onCancel,
  onDismiss,
}: UploadProgressProps) {
  const [isVisible, setIsVisible] = useState(true)
  
  // Auto-dismiss after 5 seconds when completed
  useEffect(() => {
    if (status === 'completed' && onDismiss) {
      const timer = setTimeout(() => {
        setIsVisible(false)
        onDismiss()
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [status, onDismiss])
  
  if (!isVisible) return null
  
  const handleDismiss = () => {
    setIsVisible(false)
    onDismiss?.()
  }
  
  const getStatusIcon = () => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={20} color="#48BB78" weight="fill" />
      case 'error':
        return <XCircle size={20} color="#E53E3E" weight="fill" />
      case 'uploading':
        return <Spinner size={20} color="#ECC94B" className="animate-spin" />
      default:
        return <File size={20} color="#A0AEC0" />
    }
  }

  const getProgressColor = () => {
    switch (status) {
      case 'completed':
        return 'green'
      case 'error':
        return 'red'
      default:
        return 'yellow'
    }
  }

  return (
    <HStack
      p={3}
      bg="gray.700"
      borderRadius="lg"
      spacing={3}
      w="100%"
    >
      <Box flexShrink={0}>
        {getStatusIcon()}
      </Box>
      <VStack flex={1} align="start" spacing={1} minW={0}>
        <HStack w="100%" justify="space-between">
          <Text fontSize="sm" color="white" noOfLines={1} flex={1}>
            {fileName}
          </Text>
          {status === 'uploading' && (
            <Text fontSize="xs" color="yellow.400" fontWeight="semibold" flexShrink={0}>
              {progress}%
            </Text>
          )}
        </HStack>
        {status === 'uploading' && (
          <Progress
            value={progress}
            size="xs"
            colorScheme={getProgressColor()}
            w="100%"
            borderRadius="full"
            bg="gray.600"
          />
        )}
        {status === 'error' && (
          <Text fontSize="xs" color="red.400" noOfLines={1}>
            {error || 'Erro no upload'}
          </Text>
        )}
        {status === 'completed' && (
          <Text fontSize="xs" color="green.400">
            Upload concluído
          </Text>
        )}
      </VStack>
      {onCancel && status === 'uploading' && (
        <IconButton
          aria-label="Cancelar upload"
          icon={<X size={16} />}
          size="xs"
          variant="ghost"
          colorScheme="red"
          onClick={onCancel}
        />
      )}
      {(status === 'completed' || status === 'error') && (
        <IconButton
          aria-label="Fechar"
          icon={<X size={16} />}
          size="xs"
          variant="ghost"
          colorScheme="gray"
          onClick={handleDismiss}
        />
      )}
    </HStack>
  )
}

interface UploadProgressListProps {
  uploads: Map<string, {
    fileName: string
    progress: number
    status: 'pending' | 'uploading' | 'completed' | 'error'
    error?: string
  }>
  onCancel?: (uploadId: string) => void
  onDismiss?: (uploadId: string) => void
}

export function UploadProgressList({ uploads, onCancel, onDismiss }: UploadProgressListProps) {
  if (uploads.size === 0) return null

  return (
    <VStack spacing={2} w="100%" align="stretch">
      {Array.from(uploads.entries()).map(([id, upload]) => (
        <UploadProgressItem
          key={id}
          {...upload}
          onCancel={onCancel ? () => onCancel(id) : undefined}
          onDismiss={onDismiss ? () => onDismiss(id) : undefined}
        />
      ))}
    </VStack>
  )
}
